import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import { useSupplierQuotesStore } from '../../../store';
import type { QuoteInput, QuoteStatus } from '../../../services/supplierCrm/quotesService';
import suppliersService, { Supplier } from '../../../services/supplierCrm/suppliersService';

const QuotesTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  const {
    quotes,
    loading,
    saving,
    fetchQuotes,
    createQuote,
    updateQuote,
    deleteQuote
  } = useSupplierQuotesStore();

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | ''>('');
  
  // Form state
  const [formSupplierId, setFormSupplierId] = useState('');
  const [formQuoteNumber, setFormQuoteNumber] = useState('');
  const [formStatus, setFormStatus] = useState<QuoteStatus>('draft');
  const [formAmount, setFormAmount] = useState('');
  const [formNotes, setFormNotes] = useState('');

  useEffect(() => {
    if (activeOrgId) {
      void fetchQuotes({ 
        organization_id: activeOrgId,
        status: statusFilter || undefined,
        limit: 50,
        offset: 0
      });
      
      void suppliersService.list({ organization_id: activeOrgId, limit: 100, offset: 0 })
        .then(res => setSuppliers(res.data ?? []))
        .catch(err => console.error('Failed to load suppliers', err));
    }
  }, [activeOrgId, statusFilter, fetchQuotes]);

  const resetForm = useCallback(() => {
    setFormSupplierId('');
    setFormQuoteNumber('');
    setFormStatus('draft');
    setFormAmount('');
    setFormNotes('');
    setEditingQuote(null);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrgId) return;

    const payload: QuoteInput = {
      organization_id: activeOrgId,
      supplier_id: formSupplierId,
      quote_number: formQuoteNumber || undefined,
      status: formStatus,
      amount: formAmount ? parseFloat(formAmount) : undefined,
      currency: 'USD',
      notes: formNotes || undefined
    };

    try {
      if (editingQuote) {
        await updateQuote(editingQuote, payload);
        toast.success('Quote updated');
      } else {
        await createQuote(payload);
        toast.success('Quote created');
      }
      setShowForm(false);
      resetForm();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save quote');
    }
  }, [activeOrgId, formSupplierId, formQuoteNumber, formStatus, formAmount, formNotes, editingQuote, createQuote, updateQuote, resetForm]);

  const handleDelete = useCallback(async (id: string) => {
    if (!activeOrgId || !window.confirm('Delete this quote?')) return;
    
    try {
      await deleteQuote(id, activeOrgId);
      toast.success('Quote deleted');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete quote');
    }
  }, [activeOrgId, deleteQuote]);

  const getStatusColor = (status?: QuoteStatus) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'expired': return 'bg-gray-100 text-gray-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  // Group quotes by status for Kanban view
  const quotesByStatus: Record<QuoteStatus, typeof quotes> = {
    draft: quotes.filter(q => q.status === 'draft'),
    pending: quotes.filter(q => q.status === 'pending'),
    accepted: quotes.filter(q => q.status === 'accepted'),
    rejected: quotes.filter(q => q.status === 'rejected'),
    expired: quotes.filter(q => q.status === 'expired')
  };

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <select
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as QuoteStatus | '')}
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
        >
          ➕ New Quote
        </button>
      </div>

      {/* Quote Form */}
      {showForm && (
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">
              {editingQuote ? 'Edit Quote' : 'New Quote'}
            </h3>
            <button onClick={() => { setShowForm(false); resetForm(); }} className="text-sm text-gray-500">
              Cancel
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Supplier *</label>
              <select
                required
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
                value={formSupplierId}
                onChange={(e) => setFormSupplierId(e.target.value)}
              >
                <option value="">Select supplier</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Quote Number</label>
              <input
                type="text"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={formQuoteNumber}
                onChange={(e) => setFormQuoteNumber(e.target.value)}
                placeholder="Q-2024-001"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Status</label>
              <select
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={formStatus}
                onChange={(e) => setFormStatus(e.target.value as QuoteStatus)}
              >
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Amount (USD)</label>
              <input
                type="number"
                step="0.01"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={formAmount}
                onChange={(e) => setFormAmount(e.target.value)}
                placeholder="10000.00"
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold text-gray-500">Notes</label>
              <textarea
                rows={3}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
              />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Saving...' : editingQuote ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Kanban Pipeline View */}
      <div className="grid gap-4 md:grid-cols-5">
        {(['draft', 'pending', 'accepted', 'rejected', 'expired'] as QuoteStatus[]).map((status) => (
          <div key={status} className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
            <h4 className="mb-3 text-sm font-semibold capitalize text-gray-700">
              {status} ({quotesByStatus[status].length})
            </h4>
            <div className="space-y-2">
              {quotesByStatus[status].map((quote) => {
                const supplier = suppliers.find(s => s.id === quote.supplier_id);
                return (
                  <div key={quote.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="mb-1 text-sm font-semibold text-gray-900">
                      {supplier?.name || 'Unknown'}
                    </div>
                    {quote.amount && (
                      <div className="mb-2 text-lg font-bold text-gray-900">
                        ${quote.amount.toLocaleString()}
                      </div>
                    )}
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleDelete(quote.id)}
                        className="text-xs text-red-600 hover:underline"
                        disabled={saving}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuotesTab;
